import { useEffect, useState } from 'react';
import { PrismWordmark } from '@/shared/ui/prism-wordmark';

import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

import { useAuthFlow } from '@/features/auth/hooks/use-auth-flow';
import { CodeStep } from '@/features/auth/ui/code-step';
import { EmailStep } from '@/features/auth/ui/email-step';

export const RegisterPage = () => {
  const {
    step,
    userEmail,
    isCodeError,
    isVerifying,
    isResending,
    isSubmitting,
    timeLeft,
    canResend,
    submitEmail,
    submitCode,
    resendCode,
    goBackToEmail,
  } = useAuthFlow();

  const [params, setParams] = useSearchParams();
  // Capture the inbound error once on mount, then immediately clean it out of
  // the URL. Reading `params.get('error')` on every render and using it
  // directly as the alert flag races against `setParams({...}, { replace:
  // true })` — the cleanup re-renders before the Alert paints and the
  // destructive banner becomes invisible. Storing it in local state separates
  // the URL hygiene from the visible UX.
  const [errorBanner, setErrorBanner] = useState<string | null>(() =>
    params.get('error'),
  );

  useEffect(() => {
    if (params.get('error')) {
      const next = new URLSearchParams(params);
      next.delete('error');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Prism by Aurous Labs — Sign in or create an account</title>
        <meta
          name="description"
          content="Join Prism to start creating amazing AI-powered content. Sign up with just your email address."
        />
      </Helmet>
      <div className="flex flex-col items-center gap-6 w-full">
        <div className="flex flex-col items-center text-center">
          <PrismWordmark />
        </div>

        <Card className="w-full max-w-md border-border border-t-2 border-t-primary/30 shadow-2xl shadow-black/50 gold-glow rounded-xl">
          <CardHeader className="flex flex-col items-center pb-6 pt-8 px-8">
            <h1 className="text-3xl font-display font-bold tracking-tight text-foreground text-center">
              {step === 'email' ? 'Welcome to Prism' : 'Enter verification code'}
            </h1>
            <p className="text-sm text-muted-foreground text-center mt-1">
              {step === 'email'
                ? 'Sign in or create an account'
                : `We sent a 6-digit code to ${userEmail}`}
            </p>
          </CardHeader>

          {errorBanner === 'callback_failed' && (
            <div className="px-8 pt-6">
              <Alert variant="destructive">
                <AlertDescription className="flex items-start justify-between gap-3">
                  <span>Something went wrong while signing you in. Please try again.</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setErrorBanner(null)}
                    aria-label="Dismiss notice"
                    className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive hover:bg-transparent"
                  >
                    <span aria-hidden="true">×</span>
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <CardContent className="px-8 pb-8">
            {step === 'email' ? (
              <EmailStep onSubmit={submitEmail} isSubmitting={isSubmitting} />
            ) : (
              <CodeStep
                email={userEmail}
                onSubmit={submitCode}
                onBack={goBackToEmail}
                onResend={resendCode}
                isVerifying={isVerifying}
                isCodeError={isCodeError}
                isResending={isResending}
                timeLeft={timeLeft}
                canResend={canResend}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
